import { PERMISSIONS } from "@/constants/permissions"
import { useGuardedMutation } from "@/hooks/useGuardedMutation"
import {
  IngestionStatus,
  KnowledgebaseFile,
  KnowledgebaseFileTag,
  KnowledgebaseFileUploadErrorHandler,
  KnowledgebaseFileUploadSuccessHandler,
} from "@/types/knowledgebase"
import { getFlagValue } from "@/utils/featureFlagStore"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import apiClient, { apiClientV2 } from "./apiClient"
import { USER_LIMITS_QUERY_KEY } from "./userService"

interface DocumentListApiItem {
  id: string
  fileName: string
  updatedAt?: Date
  expiryDate?: string | null
  mimetype?: string
  organization?: string
  source?: KnowledgebaseFile["source"]
  tagIds?: string[]
  tags?: KnowledgebaseFileTag[]
  fileSizeBytes?: number
  ingestionStatus?: IngestionStatus
  ingestionStep?: string
  ingestionError?: string
  ingestionIsRetryable?: boolean
  createdAt?: Date
}

interface DocumentListApiResponse {
  items: DocumentListApiItem[]
  total: number
  page: number
  pageSize: number
}

export interface GetKnowledgebaseFilesOptions {
  include?: Array<"tags">
}

interface UseGetKnowledgebaseFilesOptions extends GetKnowledgebaseFilesOptions {
  pollWhileProcessing?: boolean
}

interface UploadDocumentResponse {
  documentId: string
  status: IngestionStatus
  message: string
  s3Key: string
}

function toKnowledgebaseFile(item: DocumentListApiItem): KnowledgebaseFile {
  const createdAt = item.createdAt ?? new Date()
  return {
    id: item.id,
    fileName: item.fileName,
    createdAt,
    updatedAt: item.updatedAt ?? createdAt,
    expiryDate: item.expiryDate ?? null,
    mimetype: item.mimetype ?? "",
    organization: item.organization ?? "",
    autoTagStatus: null,
    source: item.source ?? "API",
    tagIds: item.tagIds ?? [],
    tags: item.tags,
    fileSizeBytes: item.fileSizeBytes,
    ingestionStatus: item.ingestionStatus,
    ingestionStep: item.ingestionStep,
    ingestionError: item.ingestionError,
    ingestionIsRetryable: item.ingestionIsRetryable,
  }
}

export const knowledgebaseApiCalls = {
  uploadFile: async (file: File): Promise<KnowledgebaseFile> => {
    const formData = new FormData()
    formData.append("file", file)

    if (!getFlagValue("RAG_V2_DOCUMENTS")) {
      const response = await apiClient.post<KnowledgebaseFile>(
        "/files",
        formData,
      )
      return response.data
    }

    const response = await apiClientV2.post<UploadDocumentResponse>(
      "/documents",
      formData,
    )
    const { documentId } = response.data
    return {
      id: documentId,
      fileName: file.name,
      createdAt: new Date(),
      updatedAt: new Date(),
      expiryDate: null,
      mimetype: file.type,
      organization: "",
      autoTagStatus: null,
      source: "API",
      ingestionStatus: "processing",
    }
  },
  retryIngestion: async (documentId: string): Promise<void> => {
    await apiClientV2.post(`/documents/${documentId}/retry`)
  },
  getFiles: async (
    options?: GetKnowledgebaseFilesOptions,
  ): Promise<KnowledgebaseFile[]> => {
    if (!getFlagValue("RAG_V2_DOCUMENTS")) {
      const response = await apiClient.get<KnowledgebaseFile[]>("/files")
      return response.data
    }
    const include = options?.include?.join(",")
    const response = await apiClientV2.get<DocumentListApiResponse>(
      "/documents",
      {
        params: include ? { include } : undefined,
      },
    )
    return response.data.items.map(toKnowledgebaseFile)
  },
  deleteFile: async (fileId: string): Promise<void> => {
    await apiClient.delete(`/files/${fileId}`)
  },
  deleteFiles: async (
    fileIds: string[],
  ): Promise<{ success: string[]; failed: Record<string, string> }> => {
    const response = await apiClient.delete("/files", {
      data: fileIds,
    })
    return response.data
  },
  getFile: async (fileId?: string): Promise<KnowledgebaseFile> => {
    if (!fileId) {
      throw new Error("File Id is required")
    }

    const response = await apiClient.get<KnowledgebaseFile>(`/files/${fileId}`)
    return response.data
  },
  getFileContent: async (fileId: string): Promise<ArrayBuffer> => {
    const response = await apiClient.get(`/files/${fileId}/content`, {
      responseType: "arraybuffer",
    })
    return response.data
  },
}

export const useUploadKnowledgebaseFile = (
  handleSuccess: KnowledgebaseFileUploadSuccessHandler,
  handleError: KnowledgebaseFileUploadErrorHandler,
  handleSettled: () => void,
) => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (file: File) => knowledgebaseApiCalls.uploadFile(file),
    onSuccess: handleSuccess,
    onError: handleError,
    onSettled: () => {
      // Keep the sidebar usage numbers fresh (also after a failed upload)
      void queryClient.invalidateQueries({ queryKey: USER_LIMITS_QUERY_KEY })
      handleSettled()
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.KB_UPLOAD)
}

export const useRetryKnowledgebaseIngestion = () => {
  const mutation = useMutation({
    mutationFn: (documentId: string) =>
      knowledgebaseApiCalls.retryIngestion(documentId),
  })
  return useGuardedMutation(mutation, PERMISSIONS.KB_UPLOAD)
}

export const useGetKnowledgebaseFiles = (
  options?: UseGetKnowledgebaseFilesOptions,
) => {
  const { pollWhileProcessing = true, ...requestOptions } = options ?? {}

  return useQuery({
    queryKey: ["files", requestOptions],
    queryFn: () => knowledgebaseApiCalls.getFiles(requestOptions),
    // Auto-poll every 5s while any document is still processing (v2 only)
    refetchInterval: pollWhileProcessing
      ? (query) => {
          const hasProcessing = query.state.data?.some(
            (f) =>
              f.ingestionStatus === "processing" ||
              f.ingestionStatus === "retrying",
          )
          return hasProcessing ? 5000 : false
        }
      : false,
  })
}

export const useDeleteKnowledgebaseFile = () => {
  const mutation = useMutation({
    mutationFn: (fileId: string) => knowledgebaseApiCalls.deleteFile(fileId),
  })
  return useGuardedMutation(mutation, PERMISSIONS.KB_DELETE)
}

export const useDeleteKnowledgebaseFiles = () => {
  const mutation = useMutation({
    mutationFn: (fileIds: string[]) =>
      knowledgebaseApiCalls.deleteFiles(fileIds),
  })
  return useGuardedMutation(mutation, PERMISSIONS.KB_DELETE)
}

export const useGetKnowledgebaseFile = (fileId?: string) => {
  return useQuery({
    queryKey: ["file", { fileId }],
    queryFn: () => knowledgebaseApiCalls.getFile(fileId),
    enabled: !!fileId,
  })
}
